<script setup lang="ts">
import { ref, onMounted } from 'vue';
import sanitizeHtml from 'sanitize-html';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
const { $scrollToHash } = useNuxtApp();

defineProps<{
  section: {
    heading: string;
    heading_wide: boolean;
    text: string;
    button?: {
      url: string;
      title: string;
      target: string;
    };
    underlay_mobile_image: number | undefined;
    image?: {
      url: string;
      name: string;
    };
  };
}>();

onMounted(() => {
  gsap.registerPlugin(ScrollTrigger);
  const container = document.querySelector('.section-text');

  if (container) {
    container.addEventListener('click', (event: Event) => {
      const target = event.target as HTMLElement;

      if (
        target.tagName === 'A' &&
        target.getAttribute('href')?.startsWith('#')
      ) {
        event.preventDefault();
        const targetEl = target;
        const linkHref = targetEl?.getAttribute('href') || '#';
        history.replaceState(null, '', linkHref);
        $scrollToHash(linkHref);
      }
    });
  }
});

const heroRef = ref<HTMLElement | null>(null);
const heroBgRef = ref<HTMLElement | null>(null);
const heroImgRef = ref<HTMLElement | null>(null);

onMounted(() => {
  if (heroRef.value) {
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: heroRef.value,
        start: 'top top',
        end: 'bottom+=600px bottom',
        scrub: 1,
      },
    });

    if (heroBgRef.value) {
      tl.to(heroBgRef.value, { yPercent: -100 });
    }
    if (heroImgRef.value) {
      tl.to(heroImgRef.value, { yPercent: 15, duration: 3 }, 0);
    }
  }
});
</script>

<template>
  <section ref="heroRef" class="relative z-10 bg-cyan-500 pt-22.5 md:pt-28">
    <span
      ref="heroBgRef"
      class="absolute inset-0 -z-20 bg-gradient-to-b from-beige"
    ></span>
    <div class="container py-12 md:py-36 lg:mt-4">
      <h1
        class="mb-3 last:mb-0 lg:mb-5"
        :class="section.heading_wide ? 'md:w-full lg:w-11/12' : 'md:w-1/2'"
      >
        {{ section.heading }}
      </h1>
      <div
        v-if="section.text || section.button"
        class="flex w-11/12 flex-col gap-5 text-xl md:w-1/2 lg:w-2/5 lg:gap-9"
      >
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div
          v-if="section.text"
          class="section-text"
          v-html="sanitizeHtml(section.text)"
        />
        <ButtonLink
          v-if="section.button"
          v-bind="section.button"
          icon="arrow-r"
          class="self-start"
        />
      </div>
      <div
        ref="heroImgRef"
        class="relative -right-[31%] bottom-0 -z-10 -mb-12 flex w-[393px] md:absolute md:right-0 md:mb-0 md:h-full md:w-1/2"
        :class="
          section.underlay_mobile_image ? `mt-[var(--topIndent)] md:mt-0` : ''
        "
        :style="
          section.underlay_mobile_image
            ? { '--topIndent': `${section.underlay_mobile_image}px` }
            : {}
        "
      >
        <Image
          v-if="section.image"
          class="mt-auto h-auto w-full object-contain object-bottom text-transparent md:w-auto"
          :data="section.image"
        />
      </div>
    </div>
  </section>
</template>
